import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, Spinner, fmtBRL } from "@/components/ui/Primitives";
import { api, formatErr, fmtDate } from "@/lib/api";
import {
  TrendingDown, TrendingUp, AlertTriangle, Wallet,
  ArrowRight, ReceiptText, Calendar, Clock, PiggyBank,
  Landmark, FileText, Banknote,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend,
} from "recharts";

const CAT_LABELS = {
  fornecedor: "Fornecedor", salario: "Salário", salario_adm: "Salários ADM",
  impostos: "Impostos", servico_beneficiamento: "Benef. Terceiros",
  venda: "Venda", frete: "Frete", sacaria: "Sacaria", energia: "Energia",
  aluguel: "Aluguel", folha: "Folha", manutencao: "Manutenção",
  materiais_escritorio: "Mat. Escritório", materiais_limpeza: "Mat. Limpeza",
  taxas_bancarias: "Taxas Bancárias", cesta_basica: "Cesta Básica", outros: "Outros",
};
const catLabel = (c) => CAT_LABELS[c] || (c ? c.replace(/_/g, " ") : "—");

function Stat({ icon: Icon, label, value, hint, tone = "ink", testid }) {
  const tones = {
    danger: "text-danger bg-red-50",
    success: "text-emerald-700 bg-emerald-50",
    warn: "text-warn bg-amber-50",
    ink: "text-ink-700 bg-ink-200/50",
    brand: "text-brand-600 bg-brand-50",
  };
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">{label}</div>
          <div className="font-mono text-2xl font-semibold text-ink-900 mt-2">{value}</div>
          {hint && <div className="text-xs text-ink-500 mt-1">{hint}</div>}
        </div>
        <div className={`w-10 h-10 rounded-md flex items-center justify-center ${tones[tone]}`}>
          <Icon size={18} />
        </div>
      </div>
    </Card>
  );
}

// Card de período curto (Hoje / 3 dias / 7 dias / 15 dias)
function BucketCard({ bucket, tone, icon: Icon, testid }) {
  const cn = bucket.caixa_necessario > 0;
  const bg = cn ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200";
  const apItems = bucket.ap_items || [];
  const arItems = bucket.ar_items || [];
  const [showMore, setShowMore] = useState(false);
  const apShown = showMore ? apItems : apItems.slice(0, 3);
  const arShown = showMore ? arItems : arItems.slice(0, 3);
  const hasMore = apItems.length > 3 || arItems.length > 3;
  return (
    <Card className={`p-5 border ${bg}`} data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon size={14} className={cn ? "text-danger" : "text-emerald-700"} />
        <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-700">{bucket.label}</div>
      </div>

      {/* A pagar */}
      <div className="mt-3">
        <div className="flex justify-between text-xs">
          <span className="text-ink-600">A pagar</span>
          <span className="font-mono font-semibold text-danger">{fmtBRL(bucket.ap_total)}</span>
        </div>
        {apShown.length > 0 && (
          <ul className="mt-1 space-y-0.5 pl-2 border-l-2 border-red-200">
            {apShown.map((it) => (
              <li key={it.id} className="text-[10px] leading-tight text-ink-600 flex justify-between gap-1" title={it.descricao}>
                <span className="truncate">{it.descricao}</span>
                <span className="font-mono text-danger/80 whitespace-nowrap">{fmtBRL(it.valor)}</span>
              </li>
            ))}
            {!showMore && apItems.length > 3 && (
              <li className="text-[10px] text-ink-500 italic">+{apItems.length - 3} mais</li>
            )}
          </ul>
        )}
      </div>

      {/* A receber */}
      <div className="mt-2">
        <div className="flex justify-between text-xs">
          <span className="text-ink-600">A receber</span>
          <span className="font-mono font-semibold text-emerald-700">{fmtBRL(bucket.ar_total)}</span>
        </div>
        {arShown.length > 0 && (
          <ul className="mt-1 space-y-0.5 pl-2 border-l-2 border-emerald-200">
            {arShown.map((it) => (
              <li key={it.id} className="text-[10px] leading-tight text-ink-600 flex justify-between gap-1" title={it.descricao}>
                <span className="truncate">{it.descricao}</span>
                <span className="font-mono text-emerald-700/80 whitespace-nowrap">{fmtBRL(it.valor)}</span>
              </li>
            ))}
            {!showMore && arItems.length > 3 && (
              <li className="text-[10px] text-ink-500 italic">+{arItems.length - 3} mais</li>
            )}
          </ul>
        )}
      </div>

      {/* Botão ver mais/menos */}
      {hasMore && (
        <button
          onClick={() => setShowMore((v) => !v)}
          className="mt-2 text-[10px] font-semibold text-brand-600 hover:underline"
          data-testid={`${testid}-toggle`}
        >
          {showMore ? "mostrar menos" : "ver todos"}
        </button>
      )}

      <div className="flex justify-between text-xs pt-2 mt-2 border-t border-ink-200">
        <span className="text-ink-700 font-semibold">Saldo</span>
        <span className={`font-mono font-bold ${bucket.saldo < 0 ? "text-danger" : "text-emerald-700"}`}>
          {fmtBRL(bucket.saldo)}
        </span>
      </div>

      {cn ? (
        <div className="mt-2 rounded-md bg-red-100 border border-red-300 px-2.5 py-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-danger">Precisa em caixa</div>
          <div className="font-mono text-lg font-bold text-danger">{fmtBRL(bucket.caixa_necessario)}</div>
        </div>
      ) : (
        <div className="mt-2 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-300 rounded-md px-2.5 py-2 text-center">
          Recebimentos cobrem
        </div>
      )}
    </Card>
  );
}

export default function FinanceOverview() {
  const [s, setS] = useState(null);
  const [outlook, setOutlook] = useState(null);
  const [saldosCaixa, setSaldosCaixa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [sum, out, saldos] = await Promise.all([
          api.get("/finance/summary"),
          api.get("/finance/short-outlook"),
          api.get("/finance/saldos-caixa"),
        ]);
        setS(sum.data);
        setOutlook(out.data);
        setSaldosCaixa(saldos.data);
      } catch (e) { setErr(formatErr(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <Layout title="Financeiro"><div className="flex justify-center py-20"><Spinner size={28} /></div></Layout>;

  const dailyChart = (outlook?.daily || []).map((d) => ({
    date: d.date.slice(5), // MM-DD
    "A pagar": d.ap,
    "A receber": d.ar,
    saldo: d.saldo_dia,
  }));

  const totalCategorias7d = (outlook?.categorias_7d || []).reduce((a, x) => a + Number(x.valor || 0), 0);

  return (
    <Layout title="Financeiro" subtitle="Visão geral e panorama de curto prazo">
      {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md mb-4">{err}</div>}

      {/* Linha 1 — resumo geral (existente) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Stat icon={TrendingDown} label="A pagar (aberto)" value={fmtBRL(s?.ap_open || 0)} hint={`${s?.ap_count || 0} contas em aberto`} tone="danger" testid="finance-ap-open" />
        <Stat icon={TrendingUp} label="A receber (aberto)" value={fmtBRL(s?.ar_open || 0)} hint={`${s?.ar_count || 0} contas em aberto`} tone="success" testid="finance-ar-open" />
        <Stat icon={AlertTriangle} label="Vencidas (a pagar)" value={fmtBRL(s?.ap_overdue || 0)} hint={`${s?.ap_overdue_count || 0} contas atrasadas`} tone="warn" testid="finance-ap-overdue" />
        <Stat icon={Wallet} label="Saldo previsto" value={fmtBRL(s?.saldo_previsto || 0)} hint="receber − pagar" tone={(s?.saldo_previsto || 0) >= 0 ? "brand" : "danger"} testid="finance-saldo-previsto" />
      </div>

      {/* Saldos Reais em Caixa (calculados: inicial + entradas - saídas) */}
      {saldosCaixa && (
        <Card className="p-5 mb-8 border-2 border-brand-100 bg-gradient-to-br from-brand-50/40 to-white" data-testid="saldos-atuais-card">
          <div className="flex items-center justify-between mb-1">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Saldos Atuais em Caixa</div>
              <div className="text-[11px] text-ink-500">Saldo Inicial (Empresa) + Recebimentos − Pagamentos</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-bold uppercase tracking-wider text-brand-700">Saldo Total Atual</div>
              <div className="font-mono text-2xl font-bold text-brand-900" data-testid="saldo-total-atual">
                {fmtBRL(saldosCaixa.saldo_total_atual || 0)}
              </div>
              <div className="text-[10px] text-ink-500 font-mono">
                inicial {fmtBRL(saldosCaixa.total_inicial || 0)} · +{fmtBRL(saldosCaixa.total_entradas || 0)} · −{fmtBRL(saldosCaixa.total_saidas || 0)}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mt-4">
            {(saldosCaixa.contas || []).map((c) => {
              const isBank = ["sicredi", "banco_brasil", "safra"].includes(c.id);
              const isCheque = c.id === "cheques";
              const border = isBank ? "border-emerald-200" : isCheque ? "border-amber-200" : "border-lime-200";
              const iconColor = isBank ? "text-emerald-700" : isCheque ? "text-amber-700" : "text-lime-700";
              const valueColor = isBank ? "text-emerald-900" : isCheque ? "text-amber-900" : "text-lime-900";
              const Icon = isBank ? Landmark : isCheque ? FileText : Banknote;
              return (
                <div key={c.id} className={`rounded-md bg-white border ${border} p-3`} data-testid={`saldo-caixa-${c.id}`}>
                  <div className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider ${iconColor}`}>
                    <Icon size={12} /> {c.label}
                  </div>
                  <div className={`font-mono text-xl font-bold ${valueColor} mt-0.5`}>{fmtBRL(c.saldo_atual)}</div>
                  <div className="text-[10px] text-ink-500 mt-2 space-y-0.5 border-t border-ink-100 pt-1.5">
                    <div className="flex justify-between"><span>Inicial</span><span className="font-mono">{fmtBRL(c.saldo_inicial)}</span></div>
                    <div className="flex justify-between text-emerald-700">
                      <span>+ Entradas{c.n_entradas > 0 ? ` (${c.n_entradas})` : ""}</span>
                      <span className="font-mono">{fmtBRL(c.entradas)}</span>
                    </div>
                    <div className="flex justify-between text-red-700">
                      <span>− Saídas{c.n_saidas > 0 ? ` (${c.n_saidas})` : ""}</span>
                      <span className="font-mono">{fmtBRL(c.saidas)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Linha 2 — Curto prazo */}
      <div className="mb-4">
        <h2 className="text-base font-heading font-bold text-ink-900 mb-1">Panorama de Curto Prazo</h2>
        <p className="text-xs text-ink-500">Quanto precisa ter em caixa em cada janela — vencimentos por data.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <BucketCard bucket={outlook?.hoje || {label:"Hoje", ap_total:0, ar_total:0, saldo:0, caixa_necessario:0}} icon={Clock} testid="bucket-hoje" />
        <BucketCard bucket={outlook?.prox3 || {label:"Próximos 3 dias", ap_total:0, ar_total:0, saldo:0, caixa_necessario:0}} icon={Calendar} testid="bucket-3d" />
        <BucketCard bucket={outlook?.prox7 || {label:"Próximos 7 dias", ap_total:0, ar_total:0, saldo:0, caixa_necessario:0}} icon={Calendar} testid="bucket-7d" />
        <BucketCard bucket={outlook?.prox15 || {label:"Próximos 15 dias", ap_total:0, ar_total:0, saldo:0, caixa_necessario:0}} icon={PiggyBank} testid="bucket-15d" />
      </div>

      {/* Linha 3 — Timeline 15 dias */}
      <Card className="p-5 mb-8" data-testid="finance-timeline-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Timeline de vencimentos</div>
            <div className="text-sm text-ink-700 font-semibold">Próximos 15 dias</div>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-400 inline-block" /> A pagar</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block" /> A receber</span>
          </div>
        </div>
        <div className="w-full h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dailyChart} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e2dc" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
              <Tooltip formatter={(v) => fmtBRL(v)} labelFormatter={(v) => `Dia ${v}`} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="A pagar" fill="#f87171" radius={[3,3,0,0]} />
              <Bar dataKey="A receber" fill="#10b981" radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Linha 4 — Duas tabelas lado a lado */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Contas vencidas */}
        <Card className="overflow-hidden" data-testid="contas-vencidas-card">
          <div className="px-5 py-4 border-b border-ink-200 bg-red-50/50">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-danger" />
              <div className="font-heading font-bold text-ink-900">Contas Vencidas (atenção)</div>
            </div>
            <div className="text-xs text-ink-500 mt-0.5">
              {outlook?.vencidas?.ap_count || 0} contas · Total: <b className="font-mono">{fmtBRL(outlook?.vencidas?.ap_total || 0)}</b>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <tbody>
                {(outlook?.contas_vencidas || []).map((c) => (
                  <tr key={c.id} className="border-b border-ink-200 last:border-0 hover:bg-stone-50" data-testid={`vencida-${c.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-ink-900 text-xs truncate max-w-xs" title={c.descricao}>{c.descricao}</div>
                      <div className="text-[10px] text-ink-500">{c.counterpart} • {catLabel(c.categoria)}</div>
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <div className="font-mono font-semibold text-danger text-sm">{fmtBRL(c.valor)}</div>
                      <div className="text-[10px] text-danger font-semibold">{c.dias_atraso}d atraso</div>
                    </td>
                  </tr>
                ))}
                {(outlook?.contas_vencidas || []).length === 0 && (
                  <tr><td className="px-4 py-8 text-center text-sm text-ink-500">Nenhuma conta vencida</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t border-ink-200 text-right">
            <Link to="/finance/payable?filter=overdue" className="text-xs text-brand-600 hover:underline">Ver todas →</Link>
          </div>
        </Card>

        {/* Próximos pagamentos */}
        <Card className="overflow-hidden" data-testid="proximos-pagam-card">
          <div className="px-5 py-4 border-b border-ink-200 bg-stone-50">
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-ink-700" />
              <div className="font-heading font-bold text-ink-900">Próximos Pagamentos</div>
            </div>
            <div className="text-xs text-ink-500 mt-0.5">Ordenados por data — próximos 30 dias</div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <tbody>
                {(outlook?.proximos_pagamentos || []).map((c) => (
                  <tr key={c.id} className="border-b border-ink-200 last:border-0 hover:bg-stone-50" data-testid={`prox-pag-${c.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-ink-900 text-xs truncate max-w-xs" title={c.descricao}>{c.descricao}</div>
                      <div className="text-[10px] text-ink-500">{c.counterpart} • {catLabel(c.categoria)}</div>
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <div className="font-mono font-semibold text-ink-900 text-sm">{fmtBRL(c.valor)}</div>
                      <div className="text-[10px] text-ink-500">
                        {fmtDate(c.data_vencimento)} • em {c.dias_para_vencer}d
                      </div>
                    </td>
                  </tr>
                ))}
                {(outlook?.proximos_pagamentos || []).length === 0 && (
                  <tr><td className="px-4 py-8 text-center text-sm text-ink-500">Nenhum pagamento nos próximos 30 dias</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t border-ink-200 text-right">
            <Link to="/finance/payable" className="text-xs text-brand-600 hover:underline">Ver todas →</Link>
          </div>
        </Card>
      </div>

      {/* Linha 5 — Categorias 7d */}
      {(outlook?.categorias_7d || []).length > 0 && (
        <Card className="p-5 mb-8" data-testid="categorias-7d-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Onde vai o caixa</div>
              <div className="text-sm text-ink-700 font-semibold">Despesas por categoria — próximos 7 dias</div>
            </div>
            <div className="text-sm font-mono font-bold text-danger">{fmtBRL(totalCategorias7d)}</div>
          </div>
          <div className="space-y-2">
            {(outlook?.categorias_7d || []).map((c) => {
              const pct = totalCategorias7d > 0 ? (c.valor / totalCategorias7d) * 100 : 0;
              return (
                <div key={c.categoria} data-testid={`cat-7d-${c.categoria}`}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium text-ink-800">{catLabel(c.categoria)}</span>
                    <span className="font-mono text-ink-700">{fmtBRL(c.valor)} <span className="text-ink-500">({pct.toFixed(0)}%)</span></span>
                  </div>
                  <div className="w-full h-2 bg-stone-100 rounded overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-red-400 to-red-600" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Linha 6 — Atalhos existentes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/finance/payable" className="block group">
          <Card className="p-6 h-full hover:-translate-y-0.5 transition-transform">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-md bg-red-50 text-danger flex items-center justify-center"><TrendingDown size={18} /></div>
              <ArrowRight size={16} className="text-ink-300 group-hover:text-brand-500 transition-colors" />
            </div>
            <h3 className="font-heading text-lg font-bold text-ink-900 mt-4">Contas a Pagar</h3>
            <p className="text-sm text-ink-500 mt-1">Fornecedores, fretes e despesas em aberto.</p>
          </Card>
        </Link>
        <Link to="/finance/receivable" className="block group">
          <Card className="p-6 h-full hover:-translate-y-0.5 transition-transform">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center"><TrendingUp size={18} /></div>
              <ArrowRight size={16} className="text-ink-300 group-hover:text-brand-500 transition-colors" />
            </div>
            <h3 className="font-heading text-lg font-bold text-ink-900 mt-4">Contas a Receber</h3>
            <p className="text-sm text-ink-500 mt-1">Vendas finalizadas aguardando pagamento.</p>
          </Card>
        </Link>
        <Link to="/finance/cashflow" className="block group">
          <Card className="p-6 h-full hover:-translate-y-0.5 transition-transform">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-md bg-brand-50 text-brand-600 flex items-center justify-center"><ReceiptText size={18} /></div>
              <ArrowRight size={16} className="text-ink-300 group-hover:text-brand-500 transition-colors" />
            </div>
            <h3 className="font-heading text-lg font-bold text-ink-900 mt-4">Fluxo de Caixa</h3>
            <p className="text-sm text-ink-500 mt-1">Movimentação efetiva mês a mês.</p>
          </Card>
        </Link>
      </div>
    </Layout>
  );
}
